import type { RiskSignal } from "./risk-signal-card-utils";
import {
  DEFAULT_HEADLINE_PROMPT,
  dispatchAskHarperIntent,
  type ChatContext,
} from "../../lib/chat-intents";

const BOOKMARK_QUEUE_KEY = "fintheon:pending-bookmarks:risk-signals";
const ARTIFACT_QUEUE_KEY = "fintheon:risk-signal-artifacts";

interface RiskSignalActionPayload {
  sourceType: "risk_signal";
  sourceId: string;
  title: string;
  summary: string;
  imageUrl?: string | null;
  metadata: Record<string, unknown>;
}

function buildPayload(signal: RiskSignal): RiskSignalActionPayload {
  return {
    sourceType: "risk_signal",
    sourceId: signal.id,
    title: signal.title,
    summary: signal.summary || signal.analysis,
    imageUrl: signal.imageUrl ?? null,
    metadata: {
      score: signal.score,
      severity: signal.severity,
      direction: signal.direction ?? "neutral",
      generatedAt: signal.generatedAt,
      source: signal.source,
      narrativeThreads: signal.narrativeThreads,
      relatedHeadlines: signal.relatedHeadlines,
    },
  };
}

function appendLocalQueue(key: string, payload: RiskSignalActionPayload) {
  try {
    const raw = localStorage.getItem(key);
    const current = raw ? (JSON.parse(raw) as RiskSignalActionPayload[]) : [];
    const next = [
      payload,
      ...current.filter((item) => item.sourceId !== payload.sourceId),
    ].slice(0, 40);
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    /* local queue is best-effort only */
  }
}

function buildRiskSignalContext(signal: RiskSignal): ChatContext {
  return {
    kind: "risk_signal",
    sourceType: "risk_signal",
    sourceId: signal.id,
    title: signal.title,
    summary: signal.summary || signal.analysis,
    source: signal.source ?? null,
    ivScore: typeof signal.score === "number" ? signal.score : null,
    imageUrl: signal.imageUrl ?? null,
  };
}

export function askHarperAboutRiskSignal(signal: RiskSignal) {
  // Preserve the legacy structured context event for any existing listeners.
  window.dispatchEvent(
    new CustomEvent("fintheon:ask-harper-risk-context", {
      detail: buildPayload(signal),
    }),
  );
  // Typed Ask Harper intent — renders a user-side risk card and starts Harper immediately.
  dispatchAskHarperIntent({
    prompt: DEFAULT_HEADLINE_PROMPT,
    context: buildRiskSignalContext(signal),
  });
}

export function queueRiskSignalBookmark(signal: RiskSignal) {
  const payload = buildPayload(signal);
  appendLocalQueue(BOOKMARK_QUEUE_KEY, payload);
  window.dispatchEvent(
    new CustomEvent("fintheon:bookmark-save", { detail: payload }),
  );
}

export function saveRiskSignalArtifact(signal: RiskSignal) {
  const payload = buildPayload(signal);
  appendLocalQueue(ARTIFACT_QUEUE_KEY, payload);
  window.dispatchEvent(
    new CustomEvent("fintheon:risk-signal-artifact", { detail: payload }),
  );
}

export async function shareRiskSignalImageCard(signal: RiskSignal) {
  const payload = buildPayload(signal);
  window.dispatchEvent(
    new CustomEvent("fintheon:risk-signal-image-card", { detail: payload }),
  );
  const text = [
    signal.title,
    signal.summary || signal.analysis,
    `Score ${signal.score.toFixed(1)} / Direction ${signal.direction ?? "neutral"}`,
    signal.imageUrl ?? signal.imageUnavailableReason ?? "",
  ]
    .filter(Boolean)
    .join("\n");
  if (!navigator.clipboard) return;
  await navigator.clipboard.writeText(text);
}
